import { useEffect, useMemo, useState } from 'react'
import { Navigate, useNavigate, useParams } from 'react-router-dom'
import { useAuth } from '../../auth/UseAuth'
import { useSettings } from '../../hooks/useSettings'
import { useFindContractsQuery, useGetContractQuery } from '../../redux/api/contractsApi'
import { emptyGuid } from '../../types/constants'
import { VehicleDetailsComponent } from './VehicleDetails/VehicleDetailsComponent'
import { VehicleServicesComponent } from './VehicleServices/VehicleServicesComponent'
import { FormBuilder, IFormField } from './FormBuilder/FormBuilder'
import { useGetWorkItemsFormsQuery, usePostWorkItemMutation } from '../../redux/api/ordersApi'
import { OrderInputModel } from '@ail/orders-api'
import { ObjectHelper } from '../../helpers/ObjectHelper'
import { LoadingSpinner } from '../common/LoadingSpinner/LoadingSpinner'
import { Loader } from '../common/Loader/Loader'

export interface ClearData {
  clearData: () => void
}

export function ResultsComponent() {
  const [formToSubmit, setFormToSubmit] = useState<OrderInputModel>({
    approvalProcessConfigId: '',
    data: {},
    formId: '',
  })
  const [formFields, setFormFields] = useState<IFormField[]>([])

  const { user } = useAuth()
  const params = useParams()
  const navigate = useNavigate()

  const workItemsForms = useGetWorkItemsFormsQuery()
  const [postWorkItem] = usePostWorkItemMutation()

  const [histories, setHistories, historiesLoading] = useSettings('iLinkContractHistory', { remote: false })
  const historyKey = user?.id ?? emptyGuid
  const historyLength = user?.isInternalUser ? 10 : 3

  const wokItemsForm = useMemo(() => {
    return workItemsForms.data?.find((f) => f.name.toLowerCase() === 'ilink main form')
  }, [workItemsForms.data])

  const history = useMemo(() => {
    return histories[historyKey] ?? []
  }, [histories, historyKey])

  const historyContract = useMemo(() => {
    return history.find(
      (c) => (params.id && c.id === params.id) || (params.vin && c.vehicle?.chassisNumber === params.vin)
    )
  }, [history, params.id, params.vin])

  const contract = useGetContractQuery(params.id!, {
    skip: !params.id || !user || !!historyContract || historiesLoading,
  })
  const findContract = useFindContractsQuery(
    { chassisNumber: params.vin },
    { skip: !params.vin || !!historyContract || !!contract.data || historiesLoading }
  )
  const currentContract = historyContract ?? contract.data ?? findContract.data?.at(0)

  const submit = async (form: OrderInputModel, captchaToken?: string) => {
    form.data['plate'] = currentContract?.vehicle?.licensePlate?.licenseNumber ?? ''
    form.data['kanton'] = currentContract?.vehicle?.licensePlate?.licenseNumber?.substring(0, 2) ?? ''
    form.data['model'] = currentContract?.vehicle?.modelName ?? ''
    form.data['chassisNumber'] = currentContract?.vehicle?.chassisNumber ?? ''
    form.data['makeName'] = currentContract?.vehicle?.makeName ?? ''

    try {
      await postWorkItem(form).unwrap()
      setFormToSubmit({
        approvalProcessConfigId: '',
        data: {},
        formId: '',
      })
      navigate('/WorkItems')
    } catch (e) {
      console.log(e, captchaToken)
    }

    return true
  }

  useEffect(() => {
    if (currentContract && !historyContract && history.every((c) => c.id !== currentContract.id)) {
      const newHistory = [...history, currentContract].slice(-historyLength)
      setHistories({
        ...histories,
        [historyKey]: newHistory,
      })
    }
  }, [currentContract, histories, history, historyContract, historyKey, historyLength, setHistories])

  useEffect(() => {
    if (!wokItemsForm) {
      return
    }

    const newFormFields: IFormField[] = []
    const sections = wokItemsForm?.formFields
      .filter((f) => f.type === 'radio' || f.type === 'select' || f.type == 'upload' || f.type === 'multiselect')
      .map(ObjectHelper.clone)

    sections.map((s) => {
      const index = parseInt(s.section.replace(/\D/g, ''))

      newFormFields.push({
        ...s,
        type: s.type === 'select' ? 'radio' : s.type,
        index,
      })
    })

    setFormFields(newFormFields)
  }, [wokItemsForm])

  if (!historiesLoading && !contract.isLoading && !findContract.isLoading && !currentContract) {
    return <Navigate to="/" />
  }

  return (
    <Loader blocking={!currentContract}>
      <div className="container">
        <div className="row ResultsComponet">
          <VehicleDetailsComponent vehicle={currentContract?.vehicle} />
          <VehicleServicesComponent contract={currentContract} />
          <div className="col-12 resultForm">
            {formFields.length > 0 && user?.isAdmin ? (
              <FormBuilder
                loading={workItemsForms.isFetching}
                submit={(form, captchaToken) => submit(form, captchaToken)}
                formToSubmit={formToSubmit}
                form={formFields}
              />
            ) : (
              <LoadingSpinner />
            )}
          </div>
        </div>
      </div>
    </Loader>
  )
}
